// src/lib/permissions.ts
/**
 * أدوات الصلاحيات — تخزين، جلب، والتحقق من صلاحيات المستخدم.
 *
 * تُخزَّن بيانات المستخدم (بما فيها الصلاحيات) في localStorage بعد تسجيل الدخول،
 * وتُحدَّث من `/auth/me` عند تحميل اللوحة وعند كل تنقّل (بدون polling).
 * الخادم هو مصدر الحقيقة؛ هذه النسخة لعناصر الواجهة فقط (تعطيل/إخفاء)،
 * والمنع الفعلي يبقى على المسارات في الخادم.
 */

const USER_KEY = "mc_user";

export type StoredUser = {
  id: number;
  username: string;
  email?: string | null;
  role: string;
  permissions: string[];
};

/* ---------------------------------------------------------------
 * مشترِكون — كي تعيد مكوّنات React الرسم عند تغيّر الصلاحيات
 * ------------------------------------------------------------- */
const listeners = new Set<() => void>();
/** يتغيّر مع كل تحديث للمستخدم المخزَّن — يستعمله useSyncExternalStore */
let snapshot = 0;

function notifyPermissionChange(): void {
  snapshot += 1;
  for (const cb of listeners) {
    try {
      cb();
    } catch {
      /* ignore */
    }
  }
}

/** الاشتراك في تغيّر الصلاحيات؛ تُعيد دالة إلغاء الاشتراك */
export function subscribePermissions(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** لقطة الحالة الحالية (رقم يتغيّر مع كل تحديث) */
export function permissionsSnapshot(): number {
  return snapshot;
}

/** حفظ بيانات المستخدم (تُستدعى بعد تسجيل الدخول) */
export function setStoredUser(user: StoredUser): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }
  } catch {
    /* ignore */
  }
  notifyPermissionChange();
}

/** جلب بيانات المستخدم المُخزنة */
export function getStoredUser(): StoredUser | null {
  try {
    if (typeof localStorage !== "undefined") {
      const raw = localStorage.getItem(USER_KEY);
      if (raw) return JSON.parse(raw);
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** مسح بيانات المستخدم (عند تسجيل الخروج) */
export function clearStoredUser(): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(USER_KEY);
    }
  } catch {
    /* ignore */
  }
  notifyPermissionChange();
}

/* ---------------------------------------------------------------
 * التحديث من الخادم
 * ------------------------------------------------------------- */
/** آخر تحديث ناجح (ms) — يمنع عاصفة طلبات عند التنقّل السريع */
let lastRefreshAt = 0;
let inFlight: Promise<StoredUser | null> | null = null;

/** أقل فاصل بين تحديثين متتاليين (ms) */
export const REFRESH_MIN_INTERVAL_MS = 10_000;

/**
 * تحديث الصلاحيات من `/auth/me`.
 * تُستدعى عند تحميل اللوحة وعند كل تنقّل؛ تتجاهل الطلب إن كان آخر تحديث قريبًا
 * (إلا مع `force`). لا تمسح المستخدم عند فشل الشبكة — فقط عند 401/403.
 */
export async function refreshStoredUser(force = false): Promise<StoredUser | null> {
  if (inFlight) return inFlight;
  const now = Date.now();
  if (!force && now - lastRefreshAt < REFRESH_MIN_INTERVAL_MS) {
    return getStoredUser();
  }
  inFlight = (async () => {
    try {
      // استيراد كسول: هذا الملفّ يُستورد في عشرات المكوّنات،
      // فلا نربطه بطبقة الشبكة إلا عند الحاجة الفعلية.
      const { default: api } = await import("@/lib/api");
      const res = await api.get("/auth/me");
      const u = res?.data;
      if (!u || typeof u.id !== "number") return getStoredUser();
      const fresh: StoredUser = {
        id: u.id,
        username: u.username,
        email: u.email ?? null,
        role: u.role,
        permissions: Array.isArray(u.permissions) ? u.permissions : [],
      };
      lastRefreshAt = Date.now();
      const before = getStoredUser();
      const changed =
        !before ||
        before.role !== fresh.role ||
        before.permissions.length !== fresh.permissions.length ||
        before.permissions.some((p, i) => p !== fresh.permissions[i]);
      if (changed) setStoredUser(fresh);
      else lastRefreshAt = Date.now();
      return fresh;
    } catch (e: any) {
      const status = e?.response?.status;
      // الجلسة انتهت أو المستخدم عُطِّل — نُسقط النسخة المحلية
      if (status === 401 || status === 403) {
        clearStoredUser();
        return null;
      }
      // خطأ شبكة عابر — نُبقي النسخة الحالية
      return getStoredUser();
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** لأغراض الاختبار فقط — تصفير مؤقّت التحديث */
export function resetRefreshThrottle(): void {
  lastRefreshAt = 0;
  inFlight = null;
}

/** هل المستخدم يملك صلاحية معينة؟ */
export function hasPermission(code: string): boolean {
  const user = getStoredUser();
  if (!user) return false;
  // super_admin يملك كل الصلاحيات
  if (user.role === "super_admin") return true;
  // "a|b" = أيّ منهما (صفحة التسجيلات المجدولة المشتركة: tv.record|radio.record — 2026-10-06)
  return code.split("|").some((c) => user.permissions.includes(c));
}

/** هل المستخدم يملك أي من الصلاحيات المُحددة؟ */
export function hasAnyPermission(codes: string[]): boolean {
  const user = getStoredUser();
  if (!user) return false;
  if (user.role === "super_admin") return true;
  return codes.some((c) => user.permissions.includes(c));
}

/** هل المستخدم أدمن (super_admin أو moderator)؟ */
export function isAdmin(): boolean {
  const user = getStoredUser();
  if (!user) return false;
  return user.role === "super_admin" || user.role === "moderator";
}

/** جلب قائمة الصلاحيات الحالية */
export function getPermissions(): string[] {
  const user = getStoredUser();
  return user?.permissions ?? [];
}
